import { useState, useMemo } from "react"
import { CohortProgramCard } from "@/components/academician/cohort-program-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type CohortStatus = "enrolling" | "starting-soon" | "in-progress" | "in_progress" | "completed" | "draft" | string
type FilterStatus = "all" | "enrolling" | "starting-soon"
type SortOption = "start-date" | "price"

interface Cohort {
  id: string
  name: string
  status: CohortStatus
  startDate: string
  duration: string
  price: string
  enrolled: number
  capacity: number
  bannerImage?: string
  href: string
  logoUrl?: string | null
  isPrivate?: boolean
  showPrice?: boolean
  showDetailsLink?: boolean
}

interface CohortsPageProps {
  /**
   * Array of cohorts to display
   */
  cohorts?: Cohort[]

  /**
   * Page title
   */
  title?: string

  /**
   * Page subtitle
   */
  subtitle?: string

  /**
   * Additional CSS classes
   */
  className?: string
}

// Default cohorts data
const defaultCohorts: Cohort[] = [
  {
    id: "1",
    name: "Customer Discovery Fundamentals",
    status: "enrolling",
    startDate: "March 15, 2024",
    duration: "8 weeks",
    price: "$499",
    enrolled: 12,
    capacity: 20,
    bannerImage: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=450&fit=crop",
    href: "/cohorts/customer-discovery"
  },
  {
    id: "2",
    name: "Problem Validation Bootcamp",
    status: "starting-soon",
    startDate: "March 1, 2024",
    duration: "6 weeks",
    price: "$399",
    enrolled: 18,
    capacity: 20,
    bannerImage: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=450&fit=crop",
    href: "/cohorts/problem-validation"
  },
  {
    id: "3",
    name: "Advanced Interview Techniques",
    status: "enrolling",
    startDate: "March 22, 2024",
    duration: "4 weeks",
    price: "$299",
    enrolled: 8,
    capacity: 15,
    bannerImage: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=450&fit=crop",
    href: "/cohorts/interview-techniques"
  },
  {
    id: "4",
    name: "Product-Market Fit Mastery",
    status: "enrolling",
    startDate: "April 5, 2024",
    duration: "10 weeks",
    price: "$699",
    enrolled: 5,
    capacity: 18,
    bannerImage: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=450&fit=crop",
    href: "/cohorts/product-market-fit"
  },
  {
    id: "5",
    name: "Lean Startup Methodology",
    status: "starting-soon",
    startDate: "March 8, 2024",
    duration: "8 weeks",
    price: "$549",
    enrolled: 14,
    capacity: 20,
    bannerImage: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=450&fit=crop",
    href: "/cohorts/lean-startup"
  },
  {
    id: "6",
    name: "Go-to-Market Strategy",
    status: "enrolling",
    startDate: "April 12, 2024",
    duration: "6 weeks",
    price: "$449",
    enrolled: 10,
    capacity: 16,
    bannerImage: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=450&fit=crop",
    href: "/cohorts/go-to-market"
  }
]

export function CohortsPage({
  cohorts = defaultCohorts,
  title = "Available Cohorts",
  subtitle = "Choose a program to start your validation journey",
  className
}: CohortsPageProps) {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all")
  const [sortOption, setSortOption] = useState<SortOption>("start-date")
  const [email, setEmail] = useState("")
  const [isSubscribed, setIsSubscribed] = useState(false)

  // Filter and sort cohorts
  const filteredAndSortedCohorts = useMemo(() => {
    let filtered = cohorts

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter(cohort => cohort.status === filterStatus)
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (sortOption === "start-date") {
        // Sort by start date (earliest first)
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      } else {
        // Sort by price (lowest first)
        const priceA = parseInt(a.price.replace(/[^0-9]/g, ""))
        const priceB = parseInt(b.price.replace(/[^0-9]/g, ""))
        return priceA - priceB
      }
    })

    // Prioritize enrolling cohorts
    return sorted.sort((a, b) => {
      if (a.status === "enrolling" && b.status !== "enrolling") return -1
      if (a.status !== "enrolling" && b.status === "enrolling") return 1
      return 0
    })
  }, [cohorts, filterStatus, sortOption])

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      setIsSubscribed(true)
      // Here you would typically send the email to your backend
      console.log("Email submitted:", email)
    }
  }

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Filters */}
        {cohorts.length > 0 && (
          <div className="mb-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
            {/* Status Filter */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-foreground mr-2 flex items-center">
                Status:
              </span>
              <Button
                variant={filterStatus === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("all")}
                className={cn(
                  "transition-colors",
                  filterStatus === "all" && "bg-primary text-primary-foreground"
                )}
              >
                All
              </Button>
              <Button
                variant={filterStatus === "enrolling" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("enrolling")}
                className={cn(
                  "transition-colors",
                  filterStatus === "enrolling" && "bg-primary text-primary-foreground"
                )}
              >
                Enrolling
              </Button>
              <Button
                variant={filterStatus === "starting-soon" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("starting-soon")}
                className={cn(
                  "transition-colors",
                  filterStatus === "starting-soon" && "bg-primary text-primary-foreground"
                )}
              >
                Starting Soon
              </Button>
            </div>

            {/* Sort Options */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-foreground mr-2 flex items-center">
                Sort:
              </span>
              <Button
                variant={sortOption === "start-date" ? "default" : "outline"}
                size="sm"
                onClick={() => setSortOption("start-date")}
                className={cn(
                  "transition-colors",
                  sortOption === "start-date" && "bg-primary text-primary-foreground"
                )}
              >
                Start Date
              </Button>
              <Button
                variant={sortOption === "price" ? "default" : "outline"}
                size="sm"
                onClick={() => setSortOption("price")}
                className={cn(
                  "transition-colors",
                  sortOption === "price" && "bg-primary text-primary-foreground"
                )}
              >
                Price
              </Button>
            </div>
          </div>
        )}

        {/* Cohort Grid */}
        {filteredAndSortedCohorts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedCohorts.map((cohort) => (
              <CohortProgramCard
                key={cohort.id}
                name={cohort.name}
                status={cohort.status}
                startDate={cohort.startDate}
                duration={cohort.duration}
                price={cohort.price}
                enrolled={cohort.enrolled}
                capacity={cohort.capacity}
                bannerImage={cohort.bannerImage}
                href={cohort.href}
                logoUrl={cohort.logoUrl}
                isPrivate={cohort.isPrivate}
                showPrice={cohort.showPrice}
                showDetailsLink={cohort.showDetailsLink}
              />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="mb-6">
                <div className="w-24 h-24 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-12 h-12 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-foreground mb-2">
                  No cohorts currently enrolling
                </h3>
                <p className="text-muted-foreground mb-8">
                  Check back soon! New cohorts are added regularly.
                </p>
              </div>

              {/* Email Signup */}
              {!isSubscribed ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="sr-only">
                      Email address
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full"
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Notify Me When Cohorts Open
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    We'll send you an email when new cohorts are available
                  </p>
                </form>
              ) : (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <p className="text-sm font-medium text-foreground">
                    ✓ You're subscribed!
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll notify you when new cohorts are available
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}